/*
<purpose>
  Lean shell for the ?popout=<uuid> window: slim header (subject + zone chip) over a fully
  actionable TicketDetailComponent. Same SPA, same providers — auth, maintenance banner and
  classification schema come free from App. The window IS the ticket (no NavBar, no footer).
</purpose>
<dependencies>
  smarttix.getTicket / getPiiIgnoreList / addPiiIgnore / downloadDraft; TicketManagerContext
  mutators (announce cross-window); ticketSync.onTicketMutated (refetch on foreign mutations);
  useTicketDetailRefresh (transient statuses); ReanalysisModal.
</dependencies>
<constraints>
  - Parallel windows: several popouts may exist; each self-syncs. Re-opening the same uuid
    focuses the existing window via the per-uuid window NAME in openTicketPopout.
  - PII posture unchanged: no reveal context here (Antworten/Archiv posture, server-enforced).
  - Status-driven gating in TicketDetailComponent stays the single action authority.
</constraints>
*/
import React, { useCallback, useEffect, useState } from 'react';
import TicketDetailComponent from './TicketDetail';
import ReanalysisModal from './ReanalysisModal';
import { ZoneChip } from './shared';
import { theme } from '../theme';
import { smarttix, UserRoles } from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { TicketDetail, ReanalysisRequest, PiiIgnoreEntry } from '../types';
import { useTicketManager } from '../contexts/TicketManagerContext';
import { useTicketDetailRefresh } from '../hooks/useTicketDetailRefresh';
import { onTicketMutated } from '../services/ticketSync';

export function openTicketPopout(uuid: string): void {
  const url = `${window.location.pathname}?popout=${encodeURIComponent(uuid)}`;
  window.open(url, `stx-popout-${uuid}`, 'width=920,height=1040');
}

interface PopoutShellProps {
  uuid: string;
  userRoles: UserRoles | null;
  simulateSystemData: boolean;
  // ⚠ The pop-out is a SEPARATE window mounting the same TicketDetail. It takes the flag as a prop
  // from the same App-level /config read the main window uses — a second independent read here is
  // what would let one window show the row while the other hides it for the same ticket.
  showSourceChannel?: boolean;
  // Tenant-wide inline-image counting rule, threaded from the same App-level /config read as
  // showSourceChannel above — this window is a SEPARATE React root and would silently desync from
  // the main window if it read config on its own.
  attachmentsCountInline?: boolean;
}

const PopoutShell: React.FC<PopoutShellProps> = ({ uuid, userRoles, simulateSystemData, showSourceChannel, attachmentsCountInline }) => {
  const tm = useTicketManager();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReanalysisModal, setShowReanalysisModal] = useState(false);
  const [piiIgnoreList, setPiiIgnoreList] = useState<PiiIgnoreEntry[]>([]);

  const refetch = useCallback(async () => {
    const detail = await smarttix.getTicket(uuid);
    setTicket(detail);
    setLoading(false);
  }, [uuid]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => { smarttix.getPiiIgnoreList().then(setPiiIgnoreList); }, []);
  useTicketDetailRefresh(ticket, setTicket);

  // Foreign-window mutations of THIS ticket (or bulk '*') → refetch. Own actions announce
  // via tm but BroadcastChannel never echoes to the posting window — no self-loops.
  useEffect(() => onTicketMutated(u => { if (u === uuid || u === '*') refetch(); }), [uuid, refetch]);

  useEffect(() => {
    if (ticket?.subject) document.title = `${ticket.subject} — Smarttix`;
  }, [ticket?.subject]);

  const runAction = async (fn: () => Promise<{ success: boolean; error?: string }>, fallbackMsg: string) => {
    setError(null);
    setActionLoading(true);
    const result = await fn();
    setActionLoading(false);
    if (result.success) await refetch();
    else setError(result.error || fallbackMsg);
  };

  return (
    <div style={{ fontFamily: theme.font.family, minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: theme.colors.bgPage }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexShrink: 0,
        padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
        backgroundColor: theme.colors.white, borderBottom: `1px solid ${theme.colors.borderLight}`,
      }}>
        <span style={{
          flex: 1, fontSize: 15, fontWeight: 600, color: theme.colors.navy,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {ticket?.subject || (loading ? '…' : '(Kein Betreff)')}
        </span>
        <ZoneChip zone={ticket?.suggestion?.replyEngine?.zone} />
      </div>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto',
        padding: theme.spacing.lg, maxWidth: 1200, width: '100%', margin: '0 auto', boxSizing: 'border-box',
      }}>
        {!ticket && !loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 15 }}>
            Ticket nicht gefunden
          </div>
        ) : (
          <TicketDetailComponent
            onInfo={setError}
            ticket={ticket as TicketDetail}
            loading={loading}
            actionLoading={actionLoading}
            error={error}
            onClearError={() => setError(null)}
            onApprove={() => runAction(() => tm.approveTicket(uuid), 'Fehler beim Genehmigen')}
            onCopyApprove={() => runAction(() => tm.copyApproveTicket(uuid), 'Fehler beim Kopieren')}
            onReject={() => setShowReanalysisModal(true)}
            onDownload={async () => {
              try { await smarttix.downloadDraft(uuid); }
              catch (err: any) { setError(formatBackendError(err, 'Herunterladen des Entwurfs')); }
            }}
            onReprocess={ticket?.status === 'pipeline_error'
              ? () => runAction(() => tm.processTicket(uuid), 'Fehler beim erneuten Verarbeiten')
              : undefined}
            onArchive={ticket && ['approved', 'sent', 'pipeline_error'].includes(ticket.status)
              ? () => runAction(() => tm.archiveTicket(uuid), 'Fehler beim Archivieren')
              : undefined}
            onRestore={ticket?.status === 'archived'
              ? () => runAction(() => tm.unarchiveTicket(uuid), 'Fehler beim Wiederherstellen')
              : undefined}
            piiIgnoreList={piiIgnoreList}
            onPiiIgnore={async (fieldType, value) => {
              await smarttix.addPiiIgnore(fieldType, value);
              setPiiIgnoreList(prev => [...prev, { id: 0, fieldType, value }]);
            }}
            onSuggestionEdited={refetch}
            simulateSystemData={simulateSystemData}
            showSourceChannel={showSourceChannel}
            attachmentsCountInline={attachmentsCountInline}
          />
        )}
      </div>

      {showReanalysisModal && ticket && (
        <ReanalysisModal
          ticketUuid={ticket.uuid}
          currentCategory={ticket.primaryAxis}
          reanalysisCount={ticket.reanalysisCount}
          reanalysisBlocked={ticket.reanalysisBlocked}
          attachments={ticket.attachments}
          ocrDecisions={ticket.ocrDecisions}
          onSubmit={async (request: ReanalysisRequest) => {
            await runAction(() => tm.requestReanalysis(uuid, request), 'Fehler beim Ablehnen');
            setShowReanalysisModal(false);
          }}
          onUnblock={() => runAction(() => tm.unblockReanalysis(uuid), 'Fehler beim Entsperren')}
          onClose={() => setShowReanalysisModal(false)}
          isSubmitting={actionLoading}
          isAdmin={!!(userRoles?.isCustomerAdmin || userRoles?.isAdmin)}
          // Same value TicketDetailComponent above receives, from the same App-level /config read:
          // the modal opens ON TOP of that detail pane, so a dropped prop here puts two different
          // attachment counts on screen at the same time for the same ticket.
          attachmentsCountInline={attachmentsCountInline}
        />
      )}
    </div>
  );
};

export default PopoutShell;
